import {Component, OnInit} from '@angular/core';
import {SerializedParamCollection, SerializedParamUtil} from "../../utils/serializedParameter";
import {APIService} from "../../services/api.service";
import {ActivatedRoute, Router} from "@angular/router";
import {Location, PlatformLocation} from "@angular/common";
import {AuthenticationService} from "../../services/authentication.service";
import {showAPIError} from "../../utils/api";
import {manipulateURLComponents} from "../../utils/url";
import {NotificationService} from "../../services/notification.service";
import {CursorToken, ObjectID, Portfolio, ProjectEnums, TemplateKey} from "../../interfaces";
import {ModalService} from "../../services/modal.service";
import {debounce, interval, Subject, Subscription} from "rxjs";
import {MetaService} from "../../services/meta.service";

@Component({
  selector: 'pfm-portfolio-list',
  templateUrl: './portfolio-list.component.html',
  styleUrls: ['./portfolio-list.component.scss']
})
export class PortfolioListComponent implements OnInit {

  status: 'loading'|'error'|'content' = 'loading';
  loading: boolean = true;
  portfolios: Portfolio[] = [];
  portfolioTotalCount: number|null = null;
  nextToken: CursorToken = "@start";
  filters: SerializedParamCollection = {
    search: { active: null, default: null, type: 'string' },
  };

  downloadPortfolio: Portfolio|null = null;

  searchObservable:   Subject<string>|null = null;
  searchSubscription: Subscription|null = null;

  metavalues: ProjectEnums|null = null;

  constructor(private api: APIService,
              private meta: MetaService,
              private router: Router,
              private platformLocation: PlatformLocation,
              private activatedRoute: ActivatedRoute,
              private location: Location,
              private modal: ModalService,
              private auth: AuthenticationService,
              private notification: NotificationService) {
  }

  ngOnInit(): void {
    this.searchObservable   = new Subject<string>();
    this.searchSubscription = this.searchObservable.pipe(debounce(() => interval(500))).subscribe(() => {
      Promise.all([
        this.fetchData(false, true),
        this.fetchCount(),
      ]).then(() => {});
    });

    this.activatedRoute.queryParams.subscribe(async params => {
      await this.initializeFromQueryParams(params);
    });
  }

  ngOnDestroy() {
    this.searchSubscription?.unsubscribe()
  }

  async initializeFromQueryParams(params: any) {
    this.filters = SerializedParamUtil.ParameterFromPath(params, this.filters);
    await Promise.all([
      this.fetchData(true, true),
      this.fetchCount(),
    ]);
  }

  async fetchData(initial: boolean, reset: boolean) {
    if (reset) {
      this.nextToken = '@start';
    }

    try {
      this.loading = true;
      if (initial) this.status = 'loading';

      const data = await this.api.listPortfolios(this.nextToken, 48, this.filters);

      if (initial) this.metavalues = await this.meta.getProjectEnums();

      if (reset) {
        this.portfolios = data.portfolios;
      } else {
        this.portfolios = [...this.portfolios, ...data.portfolios];
      }
      this.nextToken = data.nextPageToken;
      this.manipulatePath();

      if (initial) this.status = 'content';
    } catch (err) {
      showAPIError(this.notification, 'Daten konnten nicht geladen werden', err);
      if (initial) this.status = 'error';
    } finally {
      this.loading = false;
    }
  }

  async fetchCount() {
    try {

      this.portfolioTotalCount = null;
      const data = await this.api.countPortfolios({});
      this.portfolioTotalCount = data.count;

    } catch (err) {
      showAPIError(this.notification, 'Daten konnten nicht geladen werden', err);
    }
  }

  manipulatePath(): void {
    this.location.go(manipulateURLComponents(this.router.url, ['portfolios', 'list'], this.filters));
  }

  async editPortfolio(item: Portfolio) {
    await this.router.navigate(['/', 'portfolios', item.id, 'edit']);
  }

  deletePortfolio(item: Portfolio) {
    this.modal.openDialog({
      title: 'Portfolio löschen',
      message: 'Das Löschen kann nicht rückgängig gemacht werden.',
      type: 'danger',
      onSubmit: async () => {
        try {
          await this.api.deletePortfolio(item.id);
          this.portfolios = this.portfolios.filter(c => c.id != item.id);
          this.notification.success('Portfolio gelöscht', item.name);
        } catch (err) {
          showAPIError(this.notification, 'Fehler beim Löschenn', err);
        }
      },
    });
  }

  translateTemplate(template: TemplateKey): string {
    if (this.metavalues === null) return template;
    for (const v of this.metavalues.portfolioTemplate) {
      if (v.value === template) return v.description;
    }
    return template;
  }
}
